/**
 * Authentication Retry Mechanism
 *
 * Provides intelligent retry logic for authentication operations
 * with exponential backoff, jitter, and circuit breaker patterns.
 */

export interface RetryConfig {
  maxRetries: number;
  baseDelayMs: number;
  maxDelayMs: number;
  exponentialBase: number;
  jitterFactor: number;
  circuitBreakerThreshold: number;
  circuitBreakerResetTimeMs: number;
}

export interface RetryAttempt {
  attemptNumber: number;
  startTime: number;
  endTime?: number;
  success: boolean;
  error?: Error;
  delayMs?: number;
}

export interface AuthRetryResult<T> {
  success: boolean;
  result?: T;
  error?: Error;
  attempts: RetryAttempt[];
  totalDuration: number;
  circuitBroken: boolean;
}

export type RetryableFunction<T> = () => Promise<T>;

export class AuthenticationRetryManager {
  private readonly config: RetryConfig;
  private circuitBreakerOpenUntil = 0;
  private consecutiveFailures = 0;
  private lastSuccessTime = 0;

  constructor(config: Partial<RetryConfig> = {}) {
    this.config = {
      maxRetries: 3,
      baseDelayMs: 1000, // 1 second
      maxDelayMs: 30000, // 30 seconds
      exponentialBase: 2,
      jitterFactor: 0.1, // 10% jitter
      circuitBreakerThreshold: 5, // Open circuit after 5 consecutive failures
      circuitBreakerResetTimeMs: 60000, // 1 minute
      ...config,
    };
  }

  /**
   * Execute a retryable authentication operation with intelligent retry logic
   */
  async executeWithRetry<T>(
    operation: RetryableFunction<T>,
    operationName = 'authentication'
  ): Promise<AuthRetryResult<T>> {
    const startTime = Date.now();
    const attempts: RetryAttempt[] = [];

    // Check circuit breaker
    if (this.isCircuitBreakerOpen()) {
      return {
        success: false,
        error: new Error(`Circuit breaker is open for ${operationName}. Try again later.`),
        attempts,
        totalDuration: 0,
        circuitBroken: true,
      };
    }

    for (let attemptNumber = 1; attemptNumber <= this.config.maxRetries + 1; attemptNumber++) {
      const attemptStartTime = Date.now();
      const attempt: RetryAttempt = {
        attemptNumber,
        startTime: attemptStartTime,
        success: false,
      };

      try {
        console.warn(
          `🔄 Attempting ${operationName} (attempt ${attemptNumber}/${this.config.maxRetries + 1})`
        );

        const result = await operation();

        attempt.endTime = Date.now();
        attempt.success = true;
        attempts.push(attempt);

        // Success - reset circuit breaker state
        this.onSuccess();

        console.warn(`✅ ${operationName} succeeded on attempt ${attemptNumber}`);

        return {
          success: true,
          result,
          attempts,
          totalDuration: Date.now() - startTime,
          circuitBroken: false,
        };
      } catch (error) {
        attempt.endTime = Date.now();
        attempt.error = error as Error;
        attempts.push(attempt);

        console.warn(`❌ ${operationName} failed on attempt ${attemptNumber}:`, error);

        // Record failure for circuit breaker
        this.onFailure();

        // Don't retry on the last attempt
        if (attemptNumber > this.config.maxRetries) {
          break;
        }

        // Check if error is retryable
        if (!this.isRetryableError(error)) {
          console.warn(`🚫 Non-retryable error for ${operationName}, stopping retries`);
          break;
        }

        // Calculate delay for next attempt
        const delayMs = this.calculateDelay(attemptNumber);

        console.warn(`⏳ Waiting ${delayMs}ms before retry ${attemptNumber + 1}`);
        await this.sleep(delayMs);

        // Note: We don't set delayMs on the attempt since it applies to the next attempt
      }
    }

    // All attempts failed
    const lastError = attempts[attempts.length - 1]?.error;

    return {
      success: false,
      error:
        lastError ??
        new Error(`${operationName} failed after ${this.config.maxRetries + 1} attempts`),
      attempts,
      totalDuration: Date.now() - startTime,
      circuitBroken: this.isCircuitBreakerOpen(),
    };
  }

  /**
   * Check if an error is retryable based on its characteristics
   */
  private isRetryableError(error: unknown): boolean {
    // Handle non-Error objects
    if (!error || typeof error !== 'object') {
      return false;
    }

    const message = (error instanceof Error ? error.message : String(error)).toLowerCase();

    // Network errors are retryable
    if (message.includes('network') || message.includes('fetch') || message.includes('timeout')) {
      return true;
    }

    // Server errors (5xx) are retryable
    if (message.includes('internal server error') || message.includes('service unavailable')) {
      return true;
    }

    // Rate limiting is retryable
    if (message.includes('rate limit') || message.includes('too many requests')) {
      return true;
    }

    // Token refresh errors might be retryable
    if (message.includes('token refresh') && !message.includes('invalid')) {
      return true;
    }

    // Auth errors that are typically not retryable
    const nonRetryablePatterns = [
      'invalid credentials',
      'unauthorized',
      'forbidden',
      'invalid token',
      'expired refresh token',
      'user not found',
      'account locked',
    ];

    if (nonRetryablePatterns.some(pattern => message.includes(pattern))) {
      return false;
    }

    // Default to retryable for unknown errors
    return true;
  }

  /**
   * Calculate delay for next retry attempt with exponential backoff and jitter
   */
  private calculateDelay(attemptNumber: number): number {
    // Exponential backoff: baseDelay * (exponentialBase ^ (attemptNumber - 1))
    const exponentialDelay =
      this.config.baseDelayMs * Math.pow(this.config.exponentialBase, attemptNumber - 1);

    // Add jitter to prevent thundering herd
    const jitter = exponentialDelay * this.config.jitterFactor * Math.random();
    const delayWithJitter = exponentialDelay + jitter;

    // Cap at maximum delay
    return Math.min(delayWithJitter, this.config.maxDelayMs);
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if circuit breaker is currently open
   */
  private isCircuitBreakerOpen(): boolean {
    return Date.now() < this.circuitBreakerOpenUntil;
  }

  /**
   * Handle successful operation - reset circuit breaker
   */
  private onSuccess(): void {
    this.consecutiveFailures = 0;
    this.circuitBreakerOpenUntil = 0;
    this.lastSuccessTime = Date.now();
  }

  /**
   * Handle failed operation - update circuit breaker state
   */
  private onFailure(): void {
    this.consecutiveFailures++;

    if (this.consecutiveFailures >= this.config.circuitBreakerThreshold) {
      this.circuitBreakerOpenUntil = Date.now() + this.config.circuitBreakerResetTimeMs;
      console.warn(
        `🔴 Circuit breaker opened due to ${this.consecutiveFailures} consecutive failures`
      );
    }
  }

  /**
   * Get current circuit breaker status
   */
  getCircuitBreakerStatus() {
    return {
      isOpen: this.isCircuitBreakerOpen(),
      consecutiveFailures: this.consecutiveFailures,
      openUntil: this.circuitBreakerOpenUntil,
      lastSuccessTime: this.lastSuccessTime,
    };
  }

  /**
   * Manually reset circuit breaker (for testing or admin purposes)
   */
  resetCircuitBreaker(): void {
    this.consecutiveFailures = 0;
    this.circuitBreakerOpenUntil = 0;
    console.warn('🟢 Circuit breaker manually reset');
  }
}

// Default singleton instance for authentication operations
export const authRetryManager = new AuthenticationRetryManager();

/**
 * Convenience function for retrying authentication operations
 */
export const retryAuthOperation = <T>(
  operation: RetryableFunction<T>,
  operationName?: string
): Promise<AuthRetryResult<T>> => {
  return authRetryManager.executeWithRetry(operation, operationName);
};

/**
 * Enhanced version of the token refresh function with retry logic
 */
export const retryTokenRefresh = async (): Promise<AuthRetryResult<object>> => {
  // Import the token refresh function - note this creates a circular dependency
  // In real implementation, you'd inject this dependency or restructure
  const { refreshAccessToken } = await import('./tokenRefresh');

  return retryAuthOperation(() => refreshAccessToken(), 'token-refresh');
};

export default AuthenticationRetryManager;
